import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Clear as ClearIcon, UploadFile } from '@mui/icons-material';
import { disableReplenishmentThreshold } from 'api/replenishment/disable-replenishment-threshold';
import { GetReplenishmentThresholdsVm } from 'api/replenishment/get-replenishment-thresholds.vm';
import { getReplenishmentThresholds } from 'api/replenishment/get-replenishment-thresholds';
import { setReplenishmentThreshold, SetReplenishmentThresholdBody } from 'api/replenishment/set-replenishment-threshold';
import SetReplenishmentThresholdDialog from 'pages/replenishment/SetReplenishmentThresholdDialog';
import { ReplenishmentThresholdVm } from 'api/replenishment/replenishment-threshold.vm';
import { GetWarehousesVm } from 'api/warehouse/get-warehouses.vm';
import { getWarehouses } from 'api/warehouse/get-warehouses';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'hooks/use-snackbar';
import { ProductVm } from 'api/catalog/product.vm';
import { LocationVm } from 'api/warehouse/location.vm';
import ProductSelectionDialog from 'components/ProductSelectionDialog';
import { LocationSelectionDialog } from 'components/LocationSelectionDialog';
import { importReplenishmentThresholdsCsv } from 'api/replenishment/import-thresholds-csv';
import ReplenishmentThresholdCsvImportDialog from './ReplenishmentThresholdCsvImportDialog';

const ReplenishmentThresholdsPage = () => {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const [thresholds, setThresholds] = useState<ReplenishmentThresholdVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string }[]>([]);
  const [thresholdToEdit, setThresholdToEdit] = useState<ReplenishmentThresholdVm | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductVm | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationVm | null>(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [csvImportDialogOpen, setCsvImportDialogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const warehouseId = searchParams.get('warehouseId');
  const productId = searchParams.get('productId');
  const locationId = searchParams.get('locationId');

  const fetchThresholds = async (offset: string | null = null, reset: boolean = false) => {
    setLoading(true);
    getReplenishmentThresholds(offset || '', warehouseId || '', locationId || '', productId || '', {
      success: (response: GetReplenishmentThresholdsVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setThresholds(reset ? response.objects : [...thresholds, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter níveis de reposição:', err);
        snackbar.show('Falha ao carregar níveis de reposição', null, 'error');
        setLoading(false);
      },
    });
  };

  const fetchWarehouses = () => {
    getWarehouses('', {
      success: (response: GetWarehousesVm) => {
        setWarehouses(response.objects);
        if (!warehouseId && response.objects.length) {
          setSearchParams((prev) => {
            prev.set('warehouseId', response.objects[0].id);
            return prev;
          });
        }
      },
      error: (err) => {
        console.error('Falha ao obter armazéns:', err);
        snackbar.show('Falha ao carregar armazéns', null, 'error');
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    if (warehouseId) {
      fetchThresholds(null, true);
    } else {
      setThresholds([]);
    }
  }, [warehouseId, locationId, productId]);

  const handleProductSelect = (product: ProductVm) => {
    setSelectedProduct(product);
    setSearchParams((prev) => {
      prev.set('productId', product.id);
      return prev;
    });
    setProductDialogOpen(false);
  };

  const handleLocationSelect = (location: LocationVm) => {
    setSelectedLocation(location);
    setSearchParams((prev) => {
      prev.set('locationId', location.id);
      return prev;
    });
    setLocationDialogOpen(false);
  };

  const handleFileImport = (file: File) => {
    importReplenishmentThresholdsCsv(warehouseId!!, file, {
      success: () => {
        snackbar.show('CSV importado com sucesso', null, 'success');
        setCsvImportDialogOpen(false);
      },
      error: () => {
        snackbar.show('Falha ao importar CSV', null, 'error');
      },
    });
  };

  const handleDisableThreshold = async (threshold: ReplenishmentThresholdVm) => {
    disableReplenishmentThreshold(threshold.warehouseId, threshold.locationId, threshold.productId, {
      success: () => {
        snackbar.show('Nível de reposição removido com sucesso', null, 'success');
        fetchThresholds(null, true);
      },
      error: (err) => {
        console.error('Falha ao remover nível de reposição:', err);
        snackbar.show('Falha ao remover nível de reposição', null, 'error');
      },
    });
  };

  const handleSetReplenishmentThreshold = async (warehouseId: string, locationId: string, productId: string, min: number, max: number) => {
    const body: SetReplenishmentThresholdBody = { locationId, productId, min, max };
    setReplenishmentThreshold(warehouseId!!, body, {
      success: () => {
        snackbar.show('Nível de reposição definido com sucesso', null, 'success');
        fetchThresholds(null, true);
        setThresholdToEdit(null);
        setOpenDialog(false);
      },
      error: () => {
        snackbar.show('Falha ao definir nível de reposição', null, 'error');
      },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Níveis de Reposição
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
            Definir Nível
          </Button>
          <Button variant="outlined" startIcon={<UploadFile />} onClick={() => setCsvImportDialogOpen(true)}>
            Importar CSV
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth>
              <InputLabel id="warehouse-select-label">Selecionar Armazém</InputLabel>
              <Select
                labelId="warehouse-select-label"
                value={warehouseId || ''}
                label="Selecionar Armazém"
                onChange={(e) => {
                  setSearchParams((prev) => {
                    prev.set('warehouseId', e.target.value);
                    return prev;
                  });
                }}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{warehouse.name}</Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="Localização"
              fullWidth
              value={selectedLocation ? selectedLocation.id : ''}
              InputProps={{
                readOnly: true,
              }}
              onClick={() => setLocationDialogOpen(true)}
              placeholder="Selecionar Localização"
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="Produto"
              fullWidth
              value={selectedProduct ? selectedProduct.name : ''}
              InputProps={{
                readOnly: true,
              }}
              onClick={() => setProductDialogOpen(true)}
              placeholder="Selecionar Produto"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              onClick={() => {
                setSearchParams((prev) => {
                  prev.delete('productId');
                  prev.delete('locationId');
                  return prev;
                });
                setSelectedProduct(null);
                setSelectedLocation(null);
              }}
              startIcon={<ClearIcon />}
              disabled={!warehouseId && !selectedProduct && !selectedLocation}
              fullWidth
              sx={{ height: '56px', whiteSpace: 'nowrap' }}
            >
              Limpar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>Localização</TableCell>
              <TableCell>Produto</TableCell>
              <TableCell align="right">Mínimo</TableCell>
              <TableCell align="right">Máximo</TableCell>
              <TableCell width="120px">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {thresholds.map((threshold) => (
              <TableRow key={`${threshold.warehouseId}-${threshold.locationId}-${threshold.productId}`} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{threshold.locationId}</Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {threshold.productSku} - {threshold.productName}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Chip label={threshold.min} color="info" variant="outlined" />
                </TableCell>
                <TableCell align="right">
                  {threshold.max ? (
                    <Chip label={threshold.max} color="success" variant="outlined" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setThresholdToEdit(threshold);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => handleDisableThreshold(threshold)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={() => fetchThresholds(nextOffset)} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : thresholds.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais níveis de reposição para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhum nível de reposição encontrado
          </Typography>
        )}
      </Box>
      <ProductSelectionDialog open={productDialogOpen} onClose={() => setProductDialogOpen(false)} onProductSelect={handleProductSelect} />
      {warehouseId && (
        <LocationSelectionDialog
          open={locationDialogOpen}
          warehouseId={warehouseId}
          onClose={() => setLocationDialogOpen(false)}
          onLocationSelect={handleLocationSelect}
        />
      )}
      {warehouseId && (
        <SetReplenishmentThresholdDialog
          open={openDialog || thresholdToEdit !== null}
          onClose={() => {
            setOpenDialog(false);
            setThresholdToEdit(null);
          }}
          onSave={handleSetReplenishmentThreshold}
          warehouseId={warehouseId}
          editThreshold={thresholdToEdit}
        />
      )}
      {warehouseId && (
        <ReplenishmentThresholdCsvImportDialog
          open={csvImportDialogOpen}
          onClose={() => setCsvImportDialogOpen(false)}
          onImport={handleFileImport}
          warehouseId={warehouseId}
          warehouseName={warehouses.find((w) => w.id === warehouseId)?.name}
        />
      )}
    </Box>
  );
};

export default ReplenishmentThresholdsPage;
